import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Layout, Row, Col, theme, App } from 'antd';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import Calculator from './Calculator';
import type { CalculatorData } from './Calculator';
import CalculatorSummary from './CalculatorSummary';
import BsrChart from './BsrChart';
import SalesPlan from './SalesPlan';
import type { SalesPlanData } from './SalesPlan';
import Budget from './Budget';
import type { BudgetCategory } from './Budget';
import ForecastAmazonUS from './ForecastAmazonUS';
import type { ForecastSummaryData } from './ForecastAmazonUS';
import Summary from './Summary';
import Notes from './Notes';
import CompetitorAnalysis from './CompetitorAnalysis';
import { loadResultById, fetchSharedResult } from '../api/client';

import { FISCAL_MONTH_KEYS } from '../utils';

const MONTH_ORDER = FISCAL_MONTH_KEYS;
// monthName в payload — канонический английский ключ ('july', 'august', ...),
// чтобы XLSX-экспорт и шеринг не зависели от UI-локали.
const MONTH_NAMES = FISCAL_MONTH_KEYS;

/** Convert old sales_plan format (objects) → new SalesPlanData (arrays). Pass-through if already new. */
function mapSalesPlan(raw: any): any {
  if (!raw) return undefined;
  // Already new format — tacos is an array
  if (Array.isArray(raw.tacos)) return raw;

  const result: any = {};

  // tacos: {m1: 20, m2: 20, ..., target: 20} → [{month:1, tacos:20}, ...]
  if (raw.tacos && typeof raw.tacos === 'object') {
    const entries: {month: number; tacos: number}[] = [];
    for (const [k, v] of Object.entries(raw.tacos)) {
      if (k === 'target') continue;
      const m = parseInt(k.replace('m', ''), 10);
      if (!isNaN(m)) entries.push({ month: m, tacos: Number(v) || 0 });
    }
    entries.sort((a, b) => a.month - b.month);
    result.tacos = entries;
    result.targetTacos = Number(raw.tacos.target) || 0;
  }

  // annualGrowth: {"2025": 0, ...} → salesGrowth: [{year, annualGrowth, monthlyGrowth}, ...]
  if (raw.annualGrowth && typeof raw.annualGrowth === 'object' && !Array.isArray(raw.annualGrowth)) {
    result.salesGrowth = Object.entries(raw.annualGrowth).map(([y, v]) => {
      const annual = Number(v) || 0;
      const mg = (Math.pow(1 + annual / 100, 1 / 12) - 1) * 100;
      return { year: Number(y), annualGrowth: annual, monthlyGrowth: +mg.toFixed(2) };
    }).sort((a, b) => a.year - b.year);
  }

  // monthlyPlan: {july: -37, ...} → monthlyGrowth2025: [{month:1, monthName:'Июль', growth:-37}, ...]
  if (raw.monthlyPlan && typeof raw.monthlyPlan === 'object') {
    result.monthlyGrowth2025 = MONTH_ORDER.map((key, i) => ({
      month: i + 1,
      monthName: MONTH_NAMES[i],
      growth: Number((raw.monthlyPlan as any)[key]) || 0,
    }));
  }

  return result;
}

function mapCalcData(raw: Record<string, unknown> | undefined) {
  if (!raw) return undefined;
  return {
    purchasePrice: raw.purchase_price as number,
    shippingCostPerKg: raw.shipping_cost_per_kg as number,
    unitWeightKg: raw.unit_weight_kg as number,
    storageFee: (raw.storage_fee ?? raw.storage_fee_per_unit) as number,
    sellingPrice: raw.selling_price as number,
    referralFee: raw.referral_fee as number,
    fbaFee: raw.fba_fee as number,
    otherFees: raw.other_fees as number,
    ppcCost: raw.ppc_cost as number,
    conversionRate: raw.conversion_rate as number,
    ppcSalesPercentage: raw.ppc_sales_percentage as number,
    returnRate: raw.return_rate as number,
    resellableReturns: raw.resellable_returns as number,
    monthlySales: raw.monthly_sales as number,
    batchQuantity: raw.batch_quantity as number,
    couponPromoEnabled: raw.coupon_promo_enabled as boolean ?? true,
    asin: (raw.parent_asin as string) || undefined,
    competitorAsins: Array.isArray(raw.competitors)
      ? (raw.competitors as { asin: string }[]).map((c) => c.asin)
      : undefined,
  };
}

const DashboardView: React.FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [calculatorData, setCalculatorData] = useState<CalculatorData | undefined>();
  const [salesPlanData, setSalesPlanData] = useState<SalesPlanData | undefined>();
  const [budgetData, setBudgetData] = useState<BudgetCategory[] | undefined>();
  const [forecastSummary, setForecastSummary] = useState<ForecastSummaryData | null>(null);
  const [asin, setAsin] = useState('');
  const [competitorAsins, setCompetitorAsins] = useState<string[]>([]);
  const [keepaLoaded, setKeepaLoaded] = useState(false);
  const [loadedResult, setLoadedResult] = useState<Record<string, unknown> | null>(null);
  const [loadKey, setLoadKey] = useState(0);
  const [loadedFileName, setLoadedFileName] = useState('');
  const [loadedResultId, setLoadedResultId] = useState<number | null>(null);
  const [notesData, setNotesData] = useState<string>('');

  useEffect(() => {
    const shareId = sessionStorage.getItem('loadShareId');
    if (shareId) {
      sessionStorage.removeItem('loadShareId');
      fetchSharedResult(shareId)
        .then((data) => {
          setLoadedResult(data);
          setLoadKey((k) => k + 1);
          setLoadedFileName((data.fileName as string) || '');
          setLoadedResultId(typeof data.id === 'number' ? data.id : null);
        })
        .catch(() => message.error(t('idea_calc.messages.load_shared_error')));
    }
  }, []);

  const handleCalcChange = useCallback((data: CalculatorData) => {
    setCalculatorData(data);
    if (data.asin) setAsin(data.asin);
    else setAsin('');
    if (data.competitorAsins?.length) setCompetitorAsins(data.competitorAsins);
    setKeepaLoaded(!!data.keepaLoaded);
  }, []);

  const handleLoadResult = useCallback(async (idOrShare: number | string) => {
    try {
      const data = typeof idOrShare === 'number'
        ? await loadResultById(idOrShare)
        : await fetchSharedResult(idOrShare); // share-токен
      setLoadedResult(data);
      setLoadKey((k) => k + 1);
      setLoadedFileName((data.fileName as string) || '');
      setLoadedResultId(typeof data.id === 'number' ? data.id : null);
    } catch {
      message.error(t('idea_calc.messages.load_calc_error'));
    }
  }, []);

  const calcInitial = useMemo(
    () => loadedResult ? mapCalcData(loadedResult.calculation_data as Record<string, unknown>) : undefined,
    [loadedResult],
  );
  const budgetInitial = useMemo(() => loadedResult?.budget_data as any, [loadedResult]);
  const salesPlanInitial = useMemo(() => mapSalesPlan(loadedResult?.sales_plan), [loadedResult]);
  const notesInitial = useMemo(() => loadedResult?.notes as string | undefined, [loadedResult]);

  // Calculate total budget from budget categories (matching Budget.tsx logic)
  const totalLaunchBudget = useMemo(() => {
    if (!budgetData) return 15000; // default
    const cogs = calculatorData ? calculatorData.purchasePrice + calculatorData.shippingPrice : 5;
    // Full unit cost for buyouts: COGS + FBA + referral fee + other fees + storage
    const fullUnitCost = calculatorData ? (
      calculatorData.purchasePrice +
      calculatorData.shippingPrice +
      calculatorData.fbaFee +
      (calculatorData.sellingPrice * calculatorData.referralFee / 100) +
      (calculatorData.sellingPrice * calculatorData.otherFees / 100) +
      calculatorData.storageFee
    ) : 0;
    return budgetData.reduce((sum, cat) => {
      return sum + cat.items.reduce((catSum, item: any) => {
        // Marketing items have special calculation
        if (item.marketingType === 'vine') {
          return catSum + (item.quantity || 0) * cogs + (item.vinePrice || 0);
        }
        if (item.marketingType === 'buyout' || item.marketingType === 'reviews') {
          // Formula: quantity × (servicePrice + fullUnitCost) × (1 + commissionPercent%)
          const perUnit = (item.unitCost || 0) + fullUnitCost;
          const subtotal = (item.quantity || 0) * perUnit;
          return catSum + subtotal + subtotal * ((item.commissionPercent || 0) / 100);
        }
        return catSum + (item.amount || 0);
      }, 0);
    }, 0);
  }, [budgetData, calculatorData]);

  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      <Header calculatorData={calculatorData} salesPlanData={salesPlanData} budgetData={budgetData} notesData={notesData} onLoadResult={handleLoadResult} loadedFileName={loadedFileName} loadedResultId={loadedResultId} />
      <Layout.Content
        style={{
          maxWidth: 1400,
          width: '100%',
          margin: '0 auto',
          padding: token.padding,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Row gutter={[16, 16]} id="calculator" align="stretch">
            <Col xs={24} lg={6} style={{ display: 'flex' }}>
              <Calculator key={`calc-${loadKey}`} onDataChange={handleCalcChange} initialData={calcInitial} />
            </Col>
            <Col xs={24} lg={11} id="budget" style={{ display: 'flex' }}>
              <Budget
                key={`budget-${loadKey}`}
                cogs={calculatorData ? calculatorData.purchasePrice + calculatorData.shippingPrice : undefined}
                fullUnitCost={calculatorData ? (
                  calculatorData.purchasePrice +
                  calculatorData.shippingPrice +
                  calculatorData.fbaFee +
                  (calculatorData.sellingPrice * calculatorData.referralFee / 100) +
                  (calculatorData.sellingPrice * calculatorData.otherFees / 100) +
                  calculatorData.storageFee
                ) : undefined}
                initialData={budgetInitial}
                onDataChange={setBudgetData}
              />
            </Col>
            <Col xs={24} lg={7} style={{ display: 'flex' }}>
              <CalculatorSummary data={calculatorData} launchBudget={totalLaunchBudget} couponPromoEnabled={calculatorData?.couponPromoEnabled} />
            </Col>
          </Row>
          {asin && keepaLoaded && <BsrChart asin={asin} />}
          {(asin || competitorAsins.length > 0) && (
            <div id="competitor-analysis">
              <CompetitorAnalysis
                mainAsin={asin}
                competitorAsins={competitorAsins}
                ourPrice={calculatorData?.sellingPrice}
                ourCogs={calculatorData ? calculatorData.purchasePrice + calculatorData.shippingPrice : 0}
              />
            </div>
          )}
          <div id="sales-plan">
            <SalesPlan
              key={`sp-${loadKey}`}
              onDataChange={setSalesPlanData}
              asin={asin}
              competitorAsins={competitorAsins}
              purchasePrice={calculatorData?.purchasePrice}
              initialData={salesPlanInitial}
            />
          </div>
          <div id="forecast">
            <ForecastAmazonUS
              calculatorData={calculatorData}
              salesPlanData={salesPlanData}
              launchBudget={totalLaunchBudget}
              onSummaryChange={setForecastSummary}
            />
          </div>
          <div id="summary">
            <Summary forecastSummary={forecastSummary} />
          </div>
          <div id="notes">
            <Notes key={`notes-${loadKey}`} initialNotes={notesInitial} onDataChange={setNotesData} />
          </div>
        </div>
      </Layout.Content>
    </Layout>
  );
};

export default DashboardView;
